import dayjs from 'dayjs';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { StockPriceResponse } from '../../../server/response/responseSchema';
import { apiClient } from '../../clientUtils/apiClient';
import { useMobxInstance } from '../../clientUtils/mobxUtil';
import ChangeBarGraph, { ChangeBarGraphItem } from '../brief/ChangeBarGraph';
import ChangeValue from '../common/ChangeValue';

class ViewStore {
    ticker: string;
    stockPrice: StockPriceResponse | null = null;
    prevMonthEndPrice: StockPriceResponse | null = null;
    prevYearEndPrice: StockPriceResponse | null = null;
    isLoading: boolean = true; // 초기 상태에서는 로딩 중으로 표시
    error: string | null = null;
    graphItems: ChangeBarGraphItem[] = [];

    constructor(ticker: string) {
        this.ticker = ticker;
        makeAutoObservable(this);
    }

    updateGraphItems() {
        if (!this.stockPrice) {
            this.graphItems = [];
            return;
        }

        const items: ChangeBarGraphItem[] = [];

        if (this.stockPrice.mtdReturn != null) {
            items.push({
                name: dayjs(this.stockPrice.date).format('M월'),
                changePercent: this.stockPrice.mtdReturn,
            });
        }

        if (this.prevMonthEndPrice) {
            items.push({
                name: dayjs(this.prevMonthEndPrice.date).format('M월'),
                changePercent: this.prevMonthEndPrice.mtdReturn || 0,
            });
        }

        if (this.stockPrice.ytdReturn != null) {
            items.push({
                name: dayjs(this.stockPrice.date).format('YYYY년'),
                changePercent: this.stockPrice.ytdReturn,
            });
        }

        if (this.prevYearEndPrice) {
            items.push({
                name: dayjs(this.prevYearEndPrice.date).format('YYYY년'),
                changePercent: this.prevYearEndPrice.ytdReturn || 0,
            });
        }

        this.graphItems = items;
    }

    async fetch() {
        this.isLoading = true;
        this.error = null;

        try {
            const [stockPrice, { prevMonthEndPrice, prevYearEndPrice }] = await Promise.all([
                apiClient.stock.public.getStockPrice.$query({
                    ticker: this.ticker,
                }),
                apiClient.stock.public.getPrevYearMonthEndPrice.$query({
                    ticker: this.ticker,
                }),
            ]);

            runInAction(() => {
                this.stockPrice = stockPrice;
                this.prevMonthEndPrice = prevMonthEndPrice;
                this.prevYearEndPrice = prevYearEndPrice;
                this.updateGraphItems();
            });
        } catch (error) {
            console.error('주식 현재 가격 조회 실패:', error);
            runInAction(() => {
                this.error = '주식 가격 정보를 불러오는데 실패했습니다.';
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }
}

type StockPriceInfoProps = {
    ticker: string;
    disableFetch?: boolean;
};

const StockPriceInfo = observer(({ ticker, disableFetch = false }: StockPriceInfoProps) => {
    const viewStore = useMobxInstance(ViewStore, ticker);

    useEffect(() => {
        if (!disableFetch && !viewStore.stockPrice) {
            viewStore.fetch();
        }
    }, [viewStore, disableFetch]);

    if (!viewStore.isLoading && !viewStore.stockPrice) {
        return (
            <div className="py-8 text-center">
                <p>가격 정보를 찾을 수 없습니다.</p>
            </div>
        );
    }

    const formatPrice = (price: number | null | undefined) => {
        if (price == null) return '-';
        return `$${price.toFixed(2)}`;
    };

    return (
        <div className="flex flex-col gap-6">
            {/* 현재 가격 */}
            <div>
                <div className="text-base-content/60 mb-1 h-6 text-sm">
                    {viewStore.isLoading ? (
                        <div className="skeleton h-6 w-32 rounded-sm"></div>
                    ) : (
                        dayjs(viewStore.stockPrice!.date).format('M월 D일(ddd)')
                    )}
                </div>
                <div
                    // 이유는 모르겠는데 너비가 넘쳐버리는 문제가 있음. flex-wrap 을 주면 해결됨
                    className="flex flex-wrap items-center justify-start gap-3"
                >
                    <div className="flex h-8 items-center text-2xl font-bold">
                        {viewStore.isLoading ? (
                            <div className="skeleton h-full w-24 rounded-sm"></div>
                        ) : (
                            formatPrice(viewStore.stockPrice?.adjustedClose)
                        )}
                    </div>
                    {/* 변동 정보 */}
                    <div className="flex h-8 items-center pt-0.5 text-base font-bold">
                        {!viewStore.isLoading && viewStore.stockPrice?.dailyReturn != null && (
                            <ChangeValue
                                value={viewStore.stockPrice.dailyReturn}
                                sign={true}
                                round={2}
                                suffix="%"
                            />
                        )}
                    </div>
                </div>
            </div>
            {/* 기간별 수익률: 로딩 중일 때 스켈레톤 보여주고, 데이터가 없을 땐 숨겨줌 */}
            {(viewStore.isLoading || viewStore.graphItems.length > 0) && (
                <div>
                    <div className="text-base-content/60 mb-2 h-6 text-sm">
                        {viewStore.isLoading ? (
                            <div className="skeleton h-6 w-32 rounded-sm"></div>
                        ) : (
                            '기간별 수익률'
                        )}
                    </div>
                    <ChangeBarGraph
                        items={viewStore.graphItems}
                        className="-mx-5 mb-2"
                        nameMinWidth={80}
                        keepItemOrder={true}
                        highligthPercent={10}
                        isShowSkeleton={viewStore.isLoading}
                        skeletonCount={2}
                    />
                </div>
            )}
        </div>
    );
});

export default StockPriceInfo;
